import {useMutation} from '@tanstack/react-query';
import {Form} from 'antd';
import {noop} from 'lodash';
import {ReactElement, useCallback} from 'react';

import {appRoute} from '../../../../../../app-route';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../provider/locale/localization';
import {useSnackbar} from '../../../../../../provider/snackbar/snackbar-hook';
import {AnalyticsTarget, track} from '../../../../../../service/analytics/analytics';
import {
    CreateReviewFormPaylaodType,
    CustomReviewError,
    fetchCreateReview,
    handleCustomReviewError,
    ReviewFormErrorType,
    ReviewPayloadType,
} from '../../../../../../service/reviews/custom-reviews';
import {useReviewFormSources} from '../../../../../../service/reviews/reviews-form-sources';
import {useUrl} from '../../../../../../util/url-hook/url-hook';
import {serializeCreateReviewForm} from '../../../review-form/review-form-helper';
import {ReviewFormFieldNamesEnum, ReviewFormFieldsType} from '../../../review-form/review-form-type';
import {CustomReviewForm} from '../custom-review-form';

export function CreateReview(): ReactElement {
    const [form] = Form.useForm<ReviewFormFieldsType>();
    const {snackbar} = useSnackbar();
    const {pushUrl} = useUrl();
    const {getLocalizedString} = useLocale();
    const {data: sources} = useReviewFormSources();

    const handleCancel = useCallback(() => {
        form.resetFields();
    }, [form]);

    const {
        mutate: createReview,
        isLoading: isReviewFormInProgress,
        error: serverError,
    } = useMutation<ReviewPayloadType, ReviewFormErrorType, CreateReviewFormPaylaodType>({
        mutationFn: (fields: CreateReviewFormPaylaodType) => fetchCreateReview(fields),
        onSuccess: () => {
            snackbar.success({
                message: <Locale stringKey="REVIEW__FORM__CREATED__SUCCESS_HEADER" />,
                description: <Locale stringKey="REVIEW__FORM__CREATED__SUCCESS_BODY" />,
            });

            pushUrl(appRoute.reviewsManagement.path);
        },
        onError: (error: unknown) => {
            if (error instanceof CustomReviewError) {
                snackbar.error((error as Error).message);
            } else {
                snackbar.error({
                    message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                    description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
                });
            }

            handleCustomReviewError(error);
        },
    });

    const handleSubmit = useCallback(async () => {
        await form
            .validateFields()
            .then((values) => {
                track(
                    AnalyticsTarget.ReviewsManagement.Reviews.CreateReview,
                    sources?.find((source) => source.id === values[ReviewFormFieldNamesEnum.source])?.name || ''
                );

                createReview(serializeCreateReviewForm(values));
            })
            .catch(noop);
    }, [createReview, form, sources]);

    return (
        <CustomReviewForm
            form={form}
            handleCancel={handleCancel}
            handleSubmit={handleSubmit}
            isReviewFormInProgress={isReviewFormInProgress}
            pageHeaderTitle={getLocalizedString('REVIEW__FORM__MODAL__HEADER__ADDING')}
            serverError={serverError}
        />
    );
}
